import { vec2, type ReadonlyVec2 } from 'gl-matrix';

import { FlyweightPool } from '@canvas/object-pool/flyweight-pool';
import { LineDirection } from '@canvas/vector-types';

export { LineDirection } from '@canvas/vector-types';

const pool = new FlyweightPool<vec2>({
  initializer: () => vec2.create(),
  capacity: 5,
});

export interface LineVec2 {
  a: vec2;
  b: vec2;
}

export function lineStart(
  position: ReadonlyVec2,
  size: ReadonlyVec2,
  direction: LineDirection,
): vec2 {
  switch (direction) {
    case LineDirection.BottomRight: {
      return vec2.fromValues(position[0] - size[0] / 2, position[1] - size[1] / 2);
    }
    case LineDirection.BottomLeft: {
      return vec2.fromValues(position[0] + size[0] / 2, position[1] - size[1] / 2);
    }
    case LineDirection.TopLeft: {
      return vec2.fromValues(position[0] + size[0] / 2, position[1] + size[1] / 2);
    }
    case LineDirection.TopRight: {
      return vec2.fromValues(position[0] - size[0] / 2, position[1] + size[1] / 2);
    }
  }
}

export function lineEnd(
  position: ReadonlyVec2,
  size: ReadonlyVec2,
  direction: LineDirection,
): vec2 {
  switch (direction) {
    case LineDirection.BottomRight: {
      return vec2.fromValues(position[0] + size[0] / 2, position[1] + size[1] / 2);
    }
    case LineDirection.BottomLeft: {
      return vec2.fromValues(position[0] - size[0] / 2, position[1] + size[1] / 2);
    }
    case LineDirection.TopLeft: {
      return vec2.fromValues(position[0] - size[0] / 2, position[1] - size[1] / 2);
    }
    case LineDirection.TopRight: {
      return vec2.fromValues(position[0] + size[0] / 2, position[1] - size[1] / 2);
    }
  }
}

export function lineDirection(start: ReadonlyVec2, end: ReadonlyVec2): LineDirection {
  if (end[0] >= start[0] && end[1] >= start[1]) {
    return LineDirection.BottomRight;
  } else if (end[0] <= start[0] && end[1] >= start[1]) {
    return LineDirection.BottomLeft;
  } else if (end[0] <= start[0] && end[1] <= start[1]) {
    return LineDirection.TopLeft;
  } else {
    return LineDirection.TopRight;
  }
}

export function lineMidpoint(start: ReadonlyVec2, end: ReadonlyVec2): vec2 {
  return vec2.fromValues((start[0] + end[0]) / 2, (start[1] + end[1]) / 2);
}

export function lineSize(start: ReadonlyVec2, end: ReadonlyVec2): vec2 {
  return vec2.fromValues(Math.abs(end[0] - start[0]), Math.abs(end[1] - start[1]));
}

/**
 * @param snapAnchor the end that remains fixed
 * @param snapTarget the end being moved (revolving around `snapAnchor`)
 */
export function lineSnapEndpoint(
  snapAnchor: ReadonlyVec2,
  snapTarget: ReadonlyVec2,
): vec2 {
  const [x1, y1] = snapAnchor;
  const [x2, y2] = snapTarget;
  const x = x2 - x1;
  const y = y2 - y1;
  const theta = Math.atan2(Math.abs(y), Math.abs(x));
  const snapTheta = (() => {
    if (theta < Math.PI / 8) {
      return 0;
    } else if (theta < (Math.PI * 3) / 8) {
      return Math.PI / 4;
    } else {
      return Math.PI / 2;
    }
  })();
  const r = Math.sqrt(x ** 2 + y ** 2);
  const snapX = snapTheta === Math.PI / 2 ? 0 : Math.cos(snapTheta) * r;
  const snapY = snapTheta === 0 ? 0 : Math.sin(snapTheta) * r;
  return vec2.fromValues((x < 0 ? -snapX : snapX) + x1, (y < 0 ? -snapY : snapY) + y1);
}

export const getPointOnLine = (
  line: LineVec2,
  distance: number,
  fromEnd = false,
): vec2 => {
  const direction = vec2.sub(
    vec2.create(),
    fromEnd ? line.a : line.b,
    fromEnd ? line.b : line.a,
  );
  vec2.normalize(direction, direction);
  return vec2.scaleAndAdd(direction, fromEnd ? line.b : line.a, direction, distance);
};

/**
 * Scale a line by its length multiplied by a given factor in both directions.
 */
export const scaleLine = (start: vec2, end: vec2, multiplier: number) => {
  const directionVector = vec2.sub(vec2.create(), end, start);
  vec2.scale(directionVector, directionVector, multiplier);
  return {
    start: vec2.sub(vec2.create(), start, directionVector),
    end: vec2.add(vec2.create(), end, directionVector),
  };
};

/**
 * Calculates the perpendicular distance between a point and a line segment. The
 * specific maths is as follows:
 * 1. Calculate the vector (dx, dy) from the start of the line segment to the end.
 * 2. Calculate the squared length of this vector (lineLengthSquared).
 * 3. If the line length is 0, return the distance between the start of the line
 *    segment and the point.
 * 4. Calculate the parameter t, which is the normalized distance along the line
 *    segment from the start to the point.
 * 5. If t is less than 0, return the distance between the start of the line
 *    segment and the point.
 * 6. If t is greater than 1, return the distance between the end of the line
 *    segment and the point.
 * 7. Otherwise, calculate the closest point on the line segment using t and
 *    return the distance between the closest point and the point.
 *
 * @param lineStart - The starting point of the line segment.
 * @param lineEnd - The ending point of the line segment.
 * @param point - The point to calculate the distance to.
 * @returns The perpendicular distance between the point and the line segment.
 */
const perpendicularDistance = (
  lineStart: ReadonlyVec2,
  lineEnd: ReadonlyVec2,
  point: ReadonlyVec2,
): number => {
  const dx = lineEnd[0] - lineStart[0];
  const dy = lineEnd[1] - lineStart[1];

  const lineLengthSquared = dx * dx + dy * dy;

  if (lineLengthSquared === 0) {
    const distX = point[0] - lineStart[0];
    const distY = point[1] - lineStart[1];
    return Math.sqrt(distX * distX + distY * distY);
  }

  const t =
    ((point[0] - lineStart[0]) * dx + (point[1] - lineStart[1]) * dy) / lineLengthSquared;

  if (t < 0) {
    const distX = point[0] - lineStart[0];
    const distY = point[1] - lineStart[1];
    return Math.sqrt(distX * distX + distY * distY);
  }

  if (t > 1) {
    const distX = point[0] - lineEnd[0];
    const distY = point[1] - lineEnd[1];
    return Math.sqrt(distX * distX + distY * distY);
  }

  const closestX = lineStart[0] + t * dx;
  const closestY = lineStart[1] + t * dy;
  const distX = point[0] - closestX;
  const distY = point[1] - closestY;

  return Math.sqrt(distX * distX + distY * distY);
};

/**
 * Finds the point with the maximum distance from the line segment defined by
 * the start and end points.
 * @param startIdx - The index of the start point.
 * @param endIdx - The index of the end point.
 * @returns The index and distance of the point with the maximum distance.
 */
export const findFurthestPointFromLineSegment = (
  startIdx: number,
  endIdx: number,
  points: ReadonlyVec2[],
): { index: number; distance: number } => {
  let maxDistance = 0;
  let index = startIdx;

  for (let i = startIdx + 1; i < endIdx; i++) {
    const distance = perpendicularDistance(points[startIdx], points[endIdx], points[i]);

    if (distance > maxDistance) {
      maxDistance = distance;
      index = i;
    }
  }

  return { index, distance: maxDistance };
};

/**
 * Calculates the unit normal vector of a line segment defined by two points.
 * The unit normal is perpendicular to the line segment and has a length of 1.
 */
export const computeUnitNormal = (
  start: ReadonlyVec2,
  end: ReadonlyVec2,
  out = vec2.create(),
) => {
  // Compute the tangent vector
  const tangentVec = vec2.sub(out, end, start);
  // Compute the normal vector
  vec2.set(out, -tangentVec[1], tangentVec[0]);
  // Compute the unit normal vector
  vec2.normalize(out, out);
  return out;
};

/**
 * Tests if two line segments are parallel (pointing in the same or opposite directions).
 *
 * Visual:
 * ```
 * Parallel lines:
 * A ●──────────● B
 * C ●──────────● D
 *
 * Not parallel:
 * A ●──────────● B
 * C ●────● D
 *     /
 * ```
 *
 * @param line1 First line segment
 * @param line2 Second line segment
 * @param tolerance How close to parallel they need to be (default: very small)
 * @returns true if the lines are parallel within tolerance
 */
export function areLinesParallel(
  line1Start: ReadonlyVec2,
  line1End: ReadonlyVec2,
  line2Start: ReadonlyVec2,
  line2End: ReadonlyVec2,
  tolerance = 1e-10,
): { isParallel: boolean; direction1: ReadonlyVec2; direction2: ReadonlyVec2 } {
  const direction1 = vec2.sub(pool.get(), line1End, line1Start);
  const direction2 = vec2.sub(pool.get(), line2End, line2Start);

  // Cross product in 2D: if lines are parallel, cross product is 0
  const crossProduct = direction1[0] * direction2[1] - direction1[1] * direction2[0];

  return {
    isParallel: Math.abs(crossProduct) < tolerance,
    direction1,
    direction2,
  };
}
